import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAmadeusApi } from '@/hooks/useAmadeusApi';
import { Loader2, Search, Hotel } from 'lucide-react';

interface HotelSuggestion {
  type: string;
  hotelId: string;
  name: string;
  iataCode?: string;
  address?: {
    cityName: string;
    countryCode: string;
  };
}

interface HotelOffer {
  hotel: {
    hotelId: string;
    name: string;
    rating?: string;
    description?: {
      text: string;
      lang: string;
    };
    amenities?: string[];
    contact?: {
      phone?: string;
      email?: string;
    };
    address?: {
      lines?: string[];
      cityName?: string;
      countryCode?: string;
      postalCode?: string;
    };
  };
  available: boolean;
  offers?: Array<{
    id: string;
    checkInDate: string;
    checkOutDate: string;
    roomType?: string;
    price: {
      currency: string;
      total: string;
    };
    policies?: {
      cancellation?: {
        description?: string;
      };
    };
  }>;
}

export default function AmadeusHotelTest() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [suggestions, setSuggestions] = useState<HotelSuggestion[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<HotelSuggestion | null>(null);
  const [hotelDetails, setHotelDetails] = useState<HotelOffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { searchHotelsByName, getHotelOffers, testConnection } = useAmadeusApi();

  useEffect(() => {
    async function initAmadeus() {
      setIsLoadingDetails(true);
      try {
        const connected = await testConnection();
        if (connected) {
          // API conectada com sucesso
        } else {
          setError('Não foi possível conectar à API Amadeus. Verifique suas credenciais e configurações de CORS.');
          toast({
            title: "Erro de conexão",
            description: "Não foi possível conectar à API Amadeus. Verifique suas credenciais e configurações de CORS.",
            variant: "destructive"
          });
        }
      } catch (error) {
        setError('Não foi possível conectar à API Amadeus. Verifique suas credenciais e configurações de CORS.');
        toast({
          title: "Erro de conexão",
          description: "Não foi possível conectar à API Amadeus. Verifique suas credenciais e configurações de CORS.",
          variant: "destructive"
        });
      } finally {
        setIsLoadingDetails(false);
      }
    }
    
    initAmadeus();
  }, [toast, testConnection]);

  const handleSearch = async () => {
    if (searchTerm.length < 3) {
      toast({
        title: "Busca inválida",
        description: "Digite pelo menos 3 caracteres para buscar.",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    setSuggestions([]);
    setHotelDetails(null);
    setError(null);
    
    try {
      const results = await searchHotelsByName(searchTerm);
      
      setSuggestions(Array.isArray(results) ? results : []);
      
      // Verificar se os resultados são simulados
      if (results.length > 0) {
        const allMockResults = results.every((hotel: HotelSuggestion) => {
          return typeof hotel.hotelId === 'string' && hotel.hotelId.startsWith('MOCK_');
        });
        
        if (allMockResults) {
          setError(`Não foi possível buscar hotéis reais. Usando dados simulados para demonstração. Possível erro de CORS ou API indisponível.`);
          toast({
            title: "Usando dados simulados",
            description: "Não foi possível buscar hotéis reais. Verifique a conexão com a API Amadeus.",
            variant: "destructive"
          });
        }
      } else if (!results || results.length === 0) {
        setError(`Nenhum hotel encontrado para "${searchTerm}"`);
        toast({
          title: "Nenhum resultado",
          description: `Não encontramos hotéis com o nome "${searchTerm}".`,
          variant: "destructive"
        });
      }
    } catch (error) {
      // Verificar se é um erro de CORS
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isCorsError = errorMessage.includes('CORS') || 
                          errorMessage.includes('Failed to fetch') || 
                          errorMessage.includes('Network Error');
      
      setError(isCorsError 
        ? 'Erro de CORS ao buscar hotéis. O navegador bloqueou a requisição por motivos de segurança.'
        : 'Ocorreu um erro ao buscar hotéis. Verifique a conexão com a API Amadeus.');
      
      toast({
        title: "Erro na busca",
        description: isCorsError 
          ? "Erro de CORS. O navegador bloqueou a requisição por motivos de segurança."
          : "Ocorreu um erro ao buscar hotéis. Verifique a conexão com a API Amadeus.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleHotelSelect = async (hotel: HotelSuggestion) => {
    if (!hotel?.hotelId) {
      toast({
        title: "Hotel inválido",
        description: "O hotel selecionado não possui um ID válido.",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedHotel(hotel);
    setIsLoadingDetails(true);
    setError(null);

    try {
      const details = await getHotelOffers(hotel.hotelId);
      
      setHotelDetails(details);
      
      if (!details) {
        setError(`Não foi possível encontrar detalhes para o hotel "${hotel.name}"`);
        toast({
          title: "Sem detalhes disponíveis",
          description: `Não foi possível encontrar detalhes para o hotel "${hotel.name}".`,
          variant: "destructive"
        });
      }
    } catch (error) {
      setError('Ocorreu um erro ao buscar os detalhes do hotel.');
      toast({
        title: "Erro ao buscar detalhes",
        description: "Ocorreu um erro ao buscar os detalhes do hotel.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Teste API Amadeus - Busca de Hotéis</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
          {error.includes('CORS') && (
            <div className="mt-2 text-sm">
              <p className="font-medium">Sobre erros de CORS:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>CORS (Cross-Origin Resource Sharing) é uma restrição de segurança do navegador.</li>
                <li>Para resolver, você pode precisar usar um proxy ou configurar a API para aceitar requisições do seu domínio.</li>
                <li>Em ambiente de desenvolvimento, você pode usar extensões do navegador para desabilitar CORS temporariamente.</li>
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mb-8 space-y-4 max-w-xl">
        <div className="flex gap-2">
          <Input
            placeholder="Digite o nome do hotel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <Button 
            onClick={handleSearch}
            disabled={isSearching || searchTerm.length < 3}
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-2">Buscar</span>
          </Button>
        </div>

        <p className="text-sm text-gray-500">
          Digite o nome do hotel (ex: "Copacabana Palace", "Hilton") e clique em buscar.
        </p>

        {isSearching && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Buscando hotéis...</span>
          </div>
        )}

        {!isSearching && suggestions.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-medium mb-3">Resultados da busca:</h3>
              <div className="space-y-2">
                {suggestions.map((hotel) => (
                  <Button
                    key={hotel.hotelId}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleHotelSelect(hotel)}
                  >
                    <Hotel className="mr-2 h-4 w-4" />
                    <div className="text-left">
                      <div className="font-medium">{hotel.name}</div>
                      {hotel.address && (
                        <div className="text-sm text-gray-500">
                          {hotel.address.cityName}, {hotel.address.countryCode}
                        </div>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {isLoadingDetails && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Carregando detalhes do hotel...</span>
          </div>
        )}

        {!isLoadingDetails && selectedHotel && !hotelDetails && (
          <Card>
            <CardContent className="p-4">
              <h2 className="text-xl font-bold mb-4">{selectedHotel.name}</h2>
              <p className="text-gray-600">
                Não foi possível carregar os detalhes deste hotel. Por favor, tente novamente mais tarde.
              </p>
            </CardContent>
          </Card>
        )}

        {hotelDetails && (
          <Card>
            <CardContent className="p-4">
              <h2 className="text-xl font-bold mb-4">{hotelDetails.hotel.name}</h2>
              
              {hotelDetails.hotel.rating && (
                <div className="mb-2">
                  <span className="font-medium">Classificação:</span> {hotelDetails.hotel.rating} estrelas
                </div>
              )}

              {hotelDetails.hotel.description?.text && (
                <div className="mb-4">
                  <h3 className="font-medium mb-1">Descrição:</h3>
                  <p className="text-gray-600">{hotelDetails.hotel.description.text}</p>
                </div>
              )}

              {hotelDetails.hotel.address && (
                <div className="mb-4">
                  <h3 className="font-medium mb-1">Endereço:</h3>
                  <p className="text-gray-600">
                    {hotelDetails.hotel.address.lines?.join(', ')}<br />
                    {hotelDetails.hotel.address.cityName && `${hotelDetails.hotel.address.cityName}, `}
                    {hotelDetails.hotel.address.countryCode}
                    {hotelDetails.hotel.address.postalCode && ` - ${hotelDetails.hotel.address.postalCode}`}
                  </p>
                </div>
              )}

              {hotelDetails.hotel.amenities && hotelDetails.hotel.amenities.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-medium mb-1">Comodidades:</h3>
                  <div className="flex flex-wrap gap-2">
                    {hotelDetails.hotel.amenities.map((amenity, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 rounded-md text-sm">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hotelDetails.offers && hotelDetails.offers.length > 0 ? (
                <div>
                  <h3 className="font-medium mb-2">Ofertas disponíveis:</h3>
                  <div className="space-y-3">
                    {hotelDetails.offers.map((offer) => (
                      <div key={offer.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            {offer.roomType && (
                              <p className="font-medium">{offer.roomType}</p>
                            )}
                            <p className="text-sm">
                              Check-in: {new Date(offer.checkInDate).toLocaleDateString()}<br />
                              Check-out: {new Date(offer.checkOutDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: offer.price.currency
                              }).format(parseFloat(offer.price.total))}
                            </p>
                          </div>
                        </div>
                        {offer.policies?.cancellation?.description && (
                          <p className="text-sm text-gray-500 mt-2">
                            {offer.policies.cancellation.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-yellow-700">
                    Não há ofertas disponíveis para este hotel no momento.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
} 